import { CustomAttributeDefinition } from '@provision/entity-types';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { DefaultOption, FormField } from 'src/components';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import { usePlatformEntityOptions, useUserGroupOptions } from 'src/graphql';
import {
  CUSTOM_ATTRIBUTE_TYPE_OPTIONS,
  CustomAttribute,
  mapAttributesDefinitionGroups,
  parseOptionsInput,
  useCustomAttributeDefinitions,
} from 'src/graphql/entities/custom-attributes';

export type CustomAttributeFormValues = Partial<Omit<CustomAttribute, 'options'>> & {
  restrictAccess: boolean;
  options?: string;
  relatedEntity?: {
    id: string;
    label: string;
  };
  group?: {
    id: string;
    label: string;
  };
  restrictedToUserGroups?: {
    id: string;
    label: string;
  }[];
};

export function useCustomAttributeForm(
  customAttributeDefinition?: CustomAttributeDefinition | null,
  formValues?: CustomAttributeFormValues,
) {
  const { customAttributeDefinitions } = useCustomAttributeDefinitions({
    partnerId: customAttributeDefinition?.partnerId,
    entity: customAttributeDefinition?.entity,
  });

  const [type, setType] = useState<DefaultOption | DefaultOption[] | null>(
    formValues?.type ?? null,
  );

  useEffect(() => {
    if (formValues?.type) {
      setType(formValues.type);
    }
  }, [formValues?.type]);

  const groups = useMemo(
    () =>
      customAttributeDefinitions
        ? mapAttributesDefinitionGroups(customAttributeDefinitions)
            // if both the partner and the platform have a group with the same name, then we will user the partners version instead.
            .filter(
              (group, index, arr) =>
                !arr.some(
                  (g, idx) =>
                    g.displayName === group.displayName &&
                    g.entity === group.entity &&
                    idx !== index &&
                    group.partnerId === PLATFORM_PARTNER_ID,
                ),
            )
            .map((group) => ({
              label: group.displayName,
              id: group.key,
            }))
        : [],
    [customAttributeDefinitions],
  );

  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);

  const { userGroupOptions, loading: userGroupOptionsLoading } = useUserGroupOptions();
  const { platformEntityOptions, loading: platformEntityOptionsLoading } =
    usePlatformEntityOptions();

  const attributeFormFields = useMemo(
    () =>
      [
        {
          type: 'textfield',
          name: 'displayName' as const,
          'data-testid': 'add-custom-attribute-name',
          label: 'Property Name',
          required: 'Property name is required',
        },
        {
          type: 'autocomplete',
          name: 'group' as const,
          'data-testid': 'add-custom-attribute-group',
          label: 'Property Group',
          options: groups,
        },
        {
          type: 'autocomplete',
          name: 'type' as const,
          'data-testid': 'add-custom-attribute-type',
          label: 'Type',
          required: 'Type is required',
          autocompleteProps: {
            loading: userGroupOptionsLoading,
            onChange: (_, value) => {
              setType(value);
            },
          },
          options: CUSTOM_ATTRIBUTE_TYPE_OPTIONS,
        },
        {
          type: 'textfield',
          name: 'options' as const,
          'data-testid': 'add-custom-attribute-options',
          label: 'Options',
          required: 'Options is required',
          textFieldProps: {
            multiline: true,
          },
          onChange: ({ target }: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            setSelectedOptions(parseOptionsInput(target?.value));
          },
          showsIf: [
            {
              name: 'type',
              value: {
                id: ['multi_select', 'dropdown'],
              },
            },
          ],
        },
        {
          type: 'autocomplete',
          name: 'relatedEntity' as const,
          'data-testid': 'add-custom-attribute-related-entity',
          label: 'Related Entity',
          required: 'Related Entity is required',
          autocompleteProps: {
            loading: platformEntityOptionsLoading,
          },
          options: platformEntityOptions,
          showsIf: [
            {
              name: 'type',
              value: {
                id: ['lookup_relational'],
              },
            },
          ],
        },
        {
          type: 'textfield',
          name: 'defaultValue' as const,
          'data-testid': 'add-custom-attribute-default-value',
          label: 'Default Value',
          textFieldProps: {
            inputProps: {
              type: getInputType(type),
            },
          },
          dependsOn: ['type'],
          showsIf: [
            {
              name: 'type',
              value: {
                id: ['short_text', 'multi_line_text', 'number'],
              },
            },
          ],
        },
        {
          type: 'autocomplete',
          name: 'defaultValue' as const,
          'data-testid': 'add-custom-attribute-default-value',
          label: 'Default Value',
          dependsOn: ['type'],
          showsIf: [
            {
              name: 'type',
              value: {
                id: ['multi_select', 'dropdown'],
              },
            },
          ],
          options: selectedOptions,
        },
        {
          type: 'switch',
          name: 'defaultValue' as const,
          'data-testid': 'add-custom-attribute-default-value',
          label: 'Default Value',
          labelPlacement: 'top',
          trueLabel: 'True',
          falseLabel: 'False',
          dependsOn: ['type'],
          showsIf: [
            {
              name: 'type',
              value: {
                id: ['checkbox'],
              },
            },
          ],
        },
        {
          type: 'switch',
          name: 'restrictAccess' as const,
          'data-testid': 'add-custom-attribute-restrict-access',
          label: 'Restrict access',
        },
        {
          type: 'autocomplete',
          name: 'restrictedToUserGroups' as const,
          'data-testid': 'add-custom-attribute-restricted-groups',
          label: 'Groups with access',
          autocompleteProps: {
            multiple: true,
            disableCloseOnSelect: true,
          },
          dependsOn: ['restrictAccess'],
          options: userGroupOptions,
          showsIf: [
            {
              name: 'restrictAccess',
              value: true,
            },
          ],
        },
      ] as FormField[],
    [
      groups,
      userGroupOptionsLoading,
      platformEntityOptionsLoading,
      platformEntityOptions,
      type,
      selectedOptions,
      userGroupOptions,
    ],
  );

  return { attributeFormFields };
}

function getInputType(type?: DefaultOption | DefaultOption[] | null) {
  if (!type) {
    return 'text';
  }

  if (typeof type === 'object' && 'id' in type) {
    return type?.id;
  }

  return 'text';
}
